/**
 * PollingIndicator — polling status display and interval selector (Pattern 8.6).
 *
 * Shows the last-refreshed timestamp (updated every second via a local timer),
 * a stale-data warning when accounts haven't been recently collected, an interval
 * selector, and a manual "Refresh Now" button.
 *
 * Accessibility (NFR-A11Y-05):
 *   - aria-live="polite" on the timestamp: screen readers announce updates without
 *     interrupting the user's current focus
 *   - role="alert" on stale warning: announced assertively when stale state first
 *     appears (browser fires the alert on DOM insertion)
 */
import { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePollingInterval, VALID_INTERVALS, type PollingInterval } from '@/hooks/usePollingInterval';
import styles from './PollingIndicator.module.css';

const INTERVAL_LABELS: Record<PollingInterval, string> = {
  15000:  '15 seconds',
  30000:  '30 seconds',
  60000:  '1 minute',
  300000: '5 minutes',
};

export interface PollingIndicatorProps {
  /** Timestamp (ms since epoch) of the last successful data fetch */
  dataUpdatedAt: number | undefined;
  /** Whether any account's collection data is stale */
  isStale: boolean;
}

function formatElapsed(dataUpdatedAt: number | undefined): string {
  if (!dataUpdatedAt) return 'Never refreshed';
  const secondsAgo = Math.floor((Date.now() - dataUpdatedAt) / 1000);
  if (secondsAgo < 5) return 'Just now';
  if (secondsAgo < 60) return `${secondsAgo}s ago`;
  const minutesAgo = Math.floor(secondsAgo / 60);
  if (minutesAgo < 60) return `${minutesAgo}m ago`;
  return `${Math.floor(minutesAgo / 60)}h ago`;
}

export function PollingIndicator({ dataUpdatedAt, isStale }: PollingIndicatorProps) {
  const [pollingInterval, setPollingInterval] = usePollingInterval();
  const [elapsed, setElapsed] = useState(() => formatElapsed(dataUpdatedAt));
  const queryClient = useQueryClient();

  // Update the elapsed display every second
  useEffect(() => {
    const timer = setInterval(() => {
      setElapsed(formatElapsed(dataUpdatedAt));
    }, 1000);
    return () => clearInterval(timer);
  }, [dataUpdatedAt]);

  // Also update immediately when dataUpdatedAt changes
  useEffect(() => {
    setElapsed(formatElapsed(dataUpdatedAt));
  }, [dataUpdatedAt]);

  const handleIntervalChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPollingInterval(parseInt(e.target.value, 10));
  };

  const handleRefreshNow = useCallback(() => {
    void queryClient.invalidateQueries();
  }, [queryClient]);

  return (
    <div className={styles.container}>
      {/* Polite region: announced on update without interrupting user */}
      <span
        className={styles.timestamp}
        aria-live="polite"
        aria-atomic="true"
      >
        Last refreshed: {elapsed}
      </span>

      {/* Assertive: announced immediately on first appearance */}
      {isStale && (
        <span role="alert" className={styles.staleWarning}>
          ⚠ Stale data — some accounts not refreshed recently
        </span>
      )}

      <div className={styles.controls}>
        <label htmlFor="polling-interval" className={styles.label}>
          Refresh every
        </label>
        <select
          id="polling-interval"
          className={styles.select}
          value={pollingInterval}
          onChange={handleIntervalChange}
          aria-label="Polling interval"
        >
          {VALID_INTERVALS.map((ms) => (
            <option key={ms} value={ms}>
              {INTERVAL_LABELS[ms]}
            </option>
          ))}
        </select>

        <button
          type="button"
          className={styles.refreshButton}
          onClick={handleRefreshNow}
          aria-label="Refresh all data now"
        >
          Refresh Now
        </button>
      </div>
    </div>
  );
}
